import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePaginatedVocabularyList } from "../../hooks/usePaginatedVocabularyList";
import {
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import useTranslation from "../../hooks/useTranslation";
import {
  computeVocabularyStage,
  formatRelativeToNow,
  streakStageKey,
} from "../../utils/vocabularyProgress";
import { TEXT_LANGUAGES } from "../../constants";
import {
  useMarkVocabularyAsForgotten,
  useMarkVocabularyAsLearned,
} from "../../hooks/useMarkVocabularyAsLearned";
import { useVocabularyStageCounts } from "../../hooks/useVocabularyStageCounts";
import {
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/solid";
import { VocabularyDto } from "../../hooks/usePaginatedVocabularyList";
import { useVocabularyDelete } from "../../hooks/useVocabularyDelete";
import { notifySnackbar } from "../../lib/notifySnackbar";
import { useTextStatistics } from "../../hooks/useTextStatistics";
import { useVocabularyStatistics } from "../../hooks/useVocabularyStatistics";
import {
  getVocabularyBaseWord,
  getVocabularyPrimaryWord,
  getVocabularyPrimaryTranslation,
  getVocabularySurfaceWord,
  normalizeVocabularyTranslationDetails,
} from "../../utils/vocabularyTranslationDetails";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { highlightVocabularyWordInSentence } from "../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../components/VocabularyPronunciationButton";
import {
  sortVocabularyProgress,
  type VocabularyProgressSortMode,
} from "../../utils/sortVocabularyProgress";
import { resolveVocabularyProgressLanguageFilters } from "../../utils/resolveVocabularyProgressLanguageFilters";

const VOCABULARY_REVIEWED_EVENT = "VOCABULARY_REVIEWED";
const LANGUAGE_FILTER_STORAGE_KEY = "vocabulary-progress-language-filters-v1";

type VocabularyStageWithAll =
  | "ALL"
  | "NEW"
  | "LEARNING"
  | "REVIEWING"
  | "MASTERED"
  | "DUE";

type DerivedVocabulary = VocabularyDto & {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  nextReviewDate: Date | null;
  isDue: boolean;
};

function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? "")
    .toLocaleLowerCase()
    .replace(/[’`´]/g, "'")
    .replace(/[‐‑‒–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

const STAGE_META: {
  id: Exclude<VocabularyStageWithAll, "ALL">;
  labelKey: string;
  descriptionKey: string;
  accent: string;
  intervalKey?: string;
}[] = [
  {
    id: "DUE",
    labelKey: "vocabulary_progress_stage_due",
    descriptionKey: "vocabulary_progress_stage_due_description",
    accent: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    id: "NEW",
    labelKey: "vocabulary_progress_stage_new",
    descriptionKey: "vocabulary_progress_stage_new_description",
    accent: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    id: "LEARNING",
    labelKey: "vocabulary_progress_stage_learning",
    descriptionKey: "vocabulary_progress_stage_learning_description",
    accent: "bg-amber-50 text-amber-700 border-amber-200",
    intervalKey: "vocabulary_progress_stage_learning_interval",
  },
  {
    id: "REVIEWING",
    labelKey: "vocabulary_progress_stage_reviewing",
    descriptionKey: "vocabulary_progress_stage_reviewing_description",
    accent: "bg-indigo-50 text-indigo-700 border-indigo-200",
    intervalKey: "vocabulary_progress_stage_reviewing_interval",
  },
  {
    id: "MASTERED",
    labelKey: "vocabulary_progress_stage_mastered",
    descriptionKey: "vocabulary_progress_stage_mastered_description",
    accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    intervalKey: "vocabulary_progress_stage_mastered_interval",
  },
];

const SRS_SCHEDULE = [
  {
    streakLabel: "0",
    translationKey: "vocabulary_progress_srs_schedule_0",
  },
  {
    streakLabel: "1",
    translationKey: "vocabulary_progress_srs_schedule_1",
  },
  {
    streakLabel: "2",
    translationKey: "vocabulary_progress_srs_schedule_2",
  },
  {
    streakLabel: "3",
    translationKey: "vocabulary_progress_srs_schedule_3",
  },
  {
    streakLabel: "4",
    translationKey: "vocabulary_progress_srs_schedule_4",
  },
  {
    streakLabel: "5+",
    translationKey: "vocabulary_progress_srs_schedule_5",
  },
];

export const Route = createFileRoute("/app/vocabulary_progress")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: VocabularyProgressPage,
});

function VocabularyProgressPage() {
  const { translate, language } = useTranslation();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const [languageFilters, setLanguageFilters] = useLocalStorage<string[]>(
    LANGUAGE_FILTER_STORAGE_KEY,
    [],
  );
  const [stageFilter, setStageFilter] =
    useState<VocabularyStageWithAll>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] =
    useState<VocabularyProgressSortMode>("REVIEW_PRIORITY");
  const [activeWorkspaceLanguage, setActiveWorkspaceLanguage] =
    useState<string>("ALL_SELECTED");
  const [workspaceLanguagePickerOpen, setWorkspaceLanguagePickerOpen] =
    useState(false);
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v2",
    false,
  );
  const [vocabularyToDelete, setVocabularyToDelete] =
    useState<DerivedVocabulary | null>(null);
  const initialOriginTab: "ALL" | "PHOTO" =
    tab === "photo" ? "PHOTO" : "ALL";
  const [originTab, setOriginTab] = useState<"ALL" | "PHOTO">(
    initialOriginTab,
  );
  const deleteVocabulary = useVocabularyDelete();
  const textStatistics = useTextStatistics();
  const vocabularyStatistics = useVocabularyStatistics();
  const numberLocale = language === "GERMAN" ? "de-DE" : "en-US";
  const formatNumber = (value: number) =>
    new Intl.NumberFormat(numberLocale).format(value);
  const updateTabSearch = (value: "ALL" | "PHOTO") => {
        navigate({
          to: "/app/vocabulary_progress",
          search: { tab: value === "PHOTO" ? "photo" : undefined },
          replace: true,
        });
      };

  // Empty selection means "all languages". Multi-select supports parallel learning
  // tracks like French + Spanish without coupling to global app language-space state.
  const effectiveLanguageFilters = useMemo(
    () =>
      resolveVocabularyProgressLanguageFilters({
        selectedLanguages: languageFilters,
        availableLanguageIds: TEXT_LANGUAGES.map((lang) => lang.id),
      }),
    [languageFilters],
  );

  const paginatedVocabularyList = usePaginatedVocabularyList({
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    word_languages:
      effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
  });
  const stageCountsQuery = useVocabularyStageCounts({
    languages:
      effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    maxStreak: 10000,
    textAgeInDays: 2000 * 365,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } =
    paginatedVocabularyList;
  const vocabularyList = useMemo(() => {
    if (!paginatedVocabularyList.isSuccess) {
      return [];
    }
    return paginatedVocabularyList.data.pages.flatMap((page) => page.results);
  }, [paginatedVocabularyList.data, paginatedVocabularyList.isSuccess]);

  useEffect(() => {
    const nextTab: "ALL" | "PHOTO" = tab === "photo" ? "PHOTO" : "ALL";
    if (originTab !== nextTab) {
      setOriginTab(nextTab);
    }
  }, [originTab, tab]);

  useEffect(() => {
    const handler = () => {
      void paginatedVocabularyList.refetch();
      void stageCountsQuery.refetch();
    };
    document.addEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    return () => {
      document.removeEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    };
  }, [paginatedVocabularyList, stageCountsQuery]);

  const derivedVocabularyList: DerivedVocabulary[] = useMemo(() => {
    return vocabularyList.map((vocabulary) => {
      const { stage, nextReviewDate, isDue } = computeVocabularyStage({
        streak: vocabulary.winning_streak ?? 0,
        lastTested: vocabulary.last_tested,
      });
      return {
        ...vocabulary,
        stage,
        nextReviewDate,
        isDue,
      };
    });
  }, [vocabularyList]);

  const languageLabelById = useMemo(
    () =>
      Object.fromEntries(
        TEXT_LANGUAGES.map((lang) => [lang.id, translate(lang.prettyTranslationKey)]),
      ) as Record<string, string>,
    [translate],
  );

  const availableLanguageCounts = useMemo(() => {
    return derivedVocabularyList.reduce<Record<string, number>>((acc, item) => {
      acc[item.word_language] = (acc[item.word_language] ?? 0) + 1;
      return acc;
    }, {});
  }, [derivedVocabularyList]);

  const selectedWorkspaceLanguages = useMemo(() => {
    if (effectiveLanguageFilters.length > 0) {
      return effectiveLanguageFilters;
    }
    return TEXT_LANGUAGES.map((lang) => lang.id);
  }, [effectiveLanguageFilters]);

  useEffect(() => {
    if (activeWorkspaceLanguage === "ALL_SELECTED") {
      return;
    }
    if (!selectedWorkspaceLanguages.includes(activeWorkspaceLanguage)) {
      setActiveWorkspaceLanguage("ALL_SELECTED");
    }
  }, [activeWorkspaceLanguage, selectedWorkspaceLanguages]);

  useEffect(() => {
    setActiveWorkspaceLanguage("ALL_SELECTED");
  }, [effectiveLanguageFilters]);

  const filteredVocabulary = useMemo(() => {
    return derivedVocabularyList.filter((vocabulary) => {
      const matchesLanguage =
        effectiveLanguageFilters.length === 0 ||
        effectiveLanguageFilters.includes(vocabulary.word_language);
      const matchesStage =
        stageFilter === "ALL" || vocabulary.stage === stageFilter;
      const normalizedSearch = normalizeSearchText(searchTerm);
      const matchesSearch =
        normalizedSearch.length === 0 ||
        normalizeSearchText(vocabulary.word).includes(normalizedSearch) ||
        normalizeSearchText(vocabulary.translation).includes(normalizedSearch) ||
        normalizeSearchText(vocabulary.sentence).includes(normalizedSearch) ||
        normalizeSearchText(
          vocabulary.translation_details?.source_surface_form ?? "",
        ).includes(normalizedSearch);

      return matchesLanguage && matchesStage && matchesSearch;
    });
  }, [
    derivedVocabularyList,
    effectiveLanguageFilters,
    stageFilter,
    searchTerm,
  ]);

  const workspaceVocabulary = useMemo(() => {
    if (activeWorkspaceLanguage === "ALL_SELECTED") {
      return filteredVocabulary;
    }
    return filteredVocabulary.filter(
      (vocabulary) => vocabulary.word_language === activeWorkspaceLanguage,
    );
  }, [activeWorkspaceLanguage, filteredVocabulary]);

  const sortedVocabulary = useMemo(() => {
    return sortVocabularyProgress(workspaceVocabulary, sortMode);
  }, [sortMode, workspaceVocabulary]);
  const visibleVocabularyCount = sortedVocabulary.length;
  const totalVocabularyCount = useMemo(() => {
    if (!paginatedVocabularyList.isSuccess) {
      return 0;
    }
    return paginatedVocabularyList.data.pages[0]?.count ?? vocabularyList.length;
  }, [paginatedVocabularyList.data, paginatedVocabularyList.isSuccess, vocabularyList.length]);
  const workspaceVisibleTotalCount = useMemo(
    () =>
      Object.values(availableLanguageCounts).reduce(
        (sum, value) => sum + value,
        0,
      ),
    [availableLanguageCounts],
  );
  const selectedLanguageSummary = useMemo(() => {
    if (effectiveLanguageFilters.length === 0) {
      return translate("spaced_preview_language_all");
    }
    const labels = effectiveLanguageFilters.map(
      (languageId) => languageLabelById[languageId] ?? languageId,
    );
    return `${labels.join(", ")} (${effectiveLanguageFilters.length})`;
  }, [
    effectiveLanguageFilters,
    languageLabelById,
    translate,
    workspaceVisibleTotalCount,
  ]);

  const stageStats = useMemo(() => {
    if (activeWorkspaceLanguage !== "ALL_SELECTED") {
      return workspaceVocabulary.reduce<Record<string, number>>((acc, vocab) => {
        acc[vocab.stage] = (acc[vocab.stage] ?? 0) + 1;
        return acc;
      }, {});
    }
    const serverCounts = stageCountsQuery.data;
    if (serverCounts) {
      return serverCounts;
    }
    return workspaceVocabulary.reduce<Record<string, number>>((acc, vocab) => {
      acc[vocab.stage] = (acc[vocab.stage] ?? 0) + 1;
      return acc;
    }, {});
  }, [activeWorkspaceLanguage, stageCountsQuery.data, workspaceVocabulary]);

  const handleDeleteRequest = (vocabulary: DerivedVocabulary) => {
    setVocabularyToDelete(vocabulary);
  };

  const handleCancelDelete = () => {
    if (deleteVocabulary.isPending) {
      return;
    }
    setVocabularyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!vocabularyToDelete) {
      return;
    }
    try {
      await deleteVocabulary.mutateAsync({
        vocabularyId: vocabularyToDelete.id,
      });
      notifySnackbar({
        message: translate("vocabulary_progress_delete_success", {
          word: vocabularyToDelete.word,
        }),
      });
      setVocabularyToDelete(null);
    } catch (error) {
      console.error(error);
      notifySnackbar({
        message: translate("vocabulary_progress_delete_error"),
      });
    }
  };

  return (
    <div className="space-y-5 px-3 py-4 md:space-y-6 md:px-4 md:py-6">
      <header className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_label")}
          </p>
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
            {translate("vocabulary_progress_title")}
          </h1>
          <p className="max-w-3xl text-sm text-slate-600 md:text-base">
            {translate("vocabulary_progress_description")}
          </p>
        </div>
        <div className="mt-4 -mx-1 overflow-x-auto px-1 pb-1">
          <div className="flex w-max gap-2 sm:w-auto sm:flex-wrap">
            <button
              type="button"
              onClick={() => {
                setOriginTab("ALL");
                updateTabSearch("ALL");
              }}
              className={`rounded-full border px-4 py-2 text-base font-semibold transition md:text-sm ${
                originTab === "ALL"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {translate("vocabulary_tab_all")}
            </button>
            <button
              type="button"
              onClick={() => {
                setOriginTab("PHOTO");
                updateTabSearch("PHOTO");
              }}
              className={`rounded-full border px-4 py-2 text-base font-semibold transition md:text-sm ${
                originTab === "PHOTO"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {translate("vocabulary_tab_photo")}
            </button>
          </div>
        </div>
      </header>

      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="pb-4 text-lg font-semibold text-slate-900">
            {translate("statistics_header_vocabulary")}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                key: "total_words_added",
                text: translate("statistics_total_words_added"),
                value: vocabularyStatistics.data?.total_created ?? 0,
              },
              {
                key: "total_words_added_last_seven_days",
                text: translate("statistics_total_words_added_last_seven_days"),
                value: vocabularyStatistics.data?.last_week_created ?? 0,
              },
              {
                key: "total_words_added_last_four_weeks",
                text: translate("statistics_total_words_added_last_four_weeks"),
                value: vocabularyStatistics.data?.last_four_weeks_created ?? 0,
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <p className="pb-2 text-3xl font-bold text-slate-900">
                  {formatNumber(item.value)}
                </p>
                <p className="text-sm font-medium text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="pb-4 text-lg font-semibold text-slate-900">
            {translate("statistics_header_texts")}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                key: "total_texts_created",
                text: translate("statistics_total_texts_created"),
                value: textStatistics.data?.total_created_texts ?? 0,
              },
              {
                key: "total_created_texts_last_week",
                text: translate("statistics_total_reated_texts_last_week"),
                value: textStatistics.data?.last_week_created_texts ?? 0,
              },
              {
                key: "total_word_count",
                text: translate("statistics_total_word_count"),
                value: textStatistics.data?.total_created_texts_length ?? 0,
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <p className="pb-2 text-3xl font-bold text-slate-900">
                  {formatNumber(item.value)}
                </p>
                <p className="text-sm font-medium text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        {STAGE_META.map((stage) => {
          const description = translate(stage.descriptionKey);
          const interval = stage.intervalKey
            ? translate(stage.intervalKey)
            : null;

          return (
            <button
              key={stage.id}
              type="button"
              onClick={() =>
                setStageFilter((current) =>
                  current === stage.id ? "ALL" : stage.id,
                )
              }
              className={`flex w-full flex-col items-start rounded-2xl border p-4 text-left transition-all ${stage.accent} ${
                stageFilter === stage.id
                  ? "ring-2 ring-offset-2 ring-slate-900"
                  : ""
              }`}
            >
              <p className="text-sm font-semibold uppercase tracking-wide">
                {translate(stage.labelKey)}
              </p>
              <p className="text-2xl font-bold md:text-3xl">
                {stageStats[stage.id] ?? 0}
              </p>
              <p
                className="mt-2 text-xs text-slate-700 text-pretty leading-snug"
                title={description}
              >
                {description}
              </p>
              {interval && (
                <p
                  className="text-[11px] text-slate-600 text-pretty leading-snug"
                  title={interval}
                >
                  {interval}
                </p>
              )}
            </button>
          );
        })}
      </section>

      <SrsInfoPanel translate={translate} />

      <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_workspace_label")}
          </p>
          <h2 className="text-lg font-semibold text-slate-900">
            {translate("vocabulary_progress_workspace_title")}
          </h2>
          <p className="text-sm text-slate-600">
            {translate("vocabulary_progress_workspace_description")}
          </p>
        </div>
        <div className="mt-3 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-slate-700">
              {translate("vocabulary_progress_workspace_selection_label")}
            </p>
            <button
              type="button"
              onClick={() => setWorkspaceLanguagePickerOpen((prev) => !prev)}
              className="rounded-full border border-slate-300 px-4 py-2 text-base font-semibold text-slate-700 md:text-sm"
            >
              {workspaceLanguagePickerOpen
                ? translate("vocabulary_progress_workspace_picker_hide")
                : translate("vocabulary_progress_workspace_picker_show")}
            </button>
          </div>
          <p className="text-sm text-slate-600">
            {translate("vocabulary_progress_workspace_active_label")}{" "}
            <span className="font-semibold text-slate-800">
              {selectedLanguageSummary}
            </span>
          </p>
          {workspaceLanguagePickerOpen && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded border-slate-300"
                  checked={effectiveLanguageFilters.length === 0}
                  onChange={() => setLanguageFilters([])}
                />
                {translate("spaced_preview_language_all")}
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {TEXT_LANGUAGES.map((lang) => {
                  const isSelected = effectiveLanguageFilters.includes(lang.id);
                  return (
                    <label
                      key={lang.id}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5 rounded border-slate-300"
                        checked={isSelected}
                        onChange={() => {
                          if (effectiveLanguageFilters.length === 0) {
                            setLanguageFilters([lang.id]);
                            return;
                          }
                          if (isSelected) {
                            setLanguageFilters(
                              effectiveLanguageFilters.filter((value) => value !== lang.id),
                            );
                            return;
                          }
                          setLanguageFilters([...effectiveLanguageFilters, lang.id]);
                        }}
                      />
                      <span>
                        {translate(lang.prettyTranslationKey)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 space-y-2">
          <p className="text-sm font-semibold text-slate-700">
            {translate("vocabulary_progress_workspace_focus_label")}
          </p>
          <p className="text-xs text-slate-500">
            {translate("vocabulary_progress_workspace_focus_hint", {
              count:
                effectiveLanguageFilters.length === 0
                  ? TEXT_LANGUAGES.length
                  : effectiveLanguageFilters.length,
            })}
          </p>
          <select
            value={activeWorkspaceLanguage}
            onChange={(event) => setActiveWorkspaceLanguage(event.target.value)}
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-base md:text-sm"
          >
            <option value="ALL_SELECTED">
              {translate("vocabulary_progress_workspace_view_all_selected")}
            </option>
            {selectedWorkspaceLanguages.map((languageId) => (
              <option key={languageId} value={languageId}>
                {languageLabelById[languageId] ?? languageId}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {translate("vocabulary_progress_workspace_total_hint", {
            total: formatNumber(totalVocabularyCount),
          })}
        </p>
      </section>

      <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-2 md:p-5 xl:grid-cols-3">
        <div className="flex w-full flex-col gap-2">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_stage")}
          </span>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label={translate("vocabulary_progress_filters_stage_all")}
              active={stageFilter === "ALL"}
              onClick={() => setStageFilter("ALL")}
            />
            {STAGE_META.map((stage) => (
              <FilterChip
                key={stage.id}
                label={translate(stage.labelKey)}
                active={stageFilter === stage.id}
                onClick={() => setStageFilter(stage.id)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full lg:w-1/3">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_search")}
          </span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={translate(
              "vocabulary_progress_filters_search_placeholder",
            )}
            className="w-full rounded-full border border-slate-200 px-4 py-2 text-base focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-100"
          />
        </div>
        <div className="flex w-full flex-col gap-2">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_sort")}
          </span>
          <select
            value={sortMode}
            onChange={(event) =>
              setSortMode(event.target.value as VocabularyProgressSortMode)
            }
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-base focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-100"
          >
            <option value="REVIEW_PRIORITY">
              {translate("vocabulary_progress_sort_review_priority")}
            </option>
            <option value="WORD_A_Z">
              {translate("vocabulary_progress_sort_word_az")}
            </option>
            <option value="WORD_Z_A">
              {translate("vocabulary_progress_sort_word_za")}
            </option>
            <option value="NEWEST_ADDED">
              {translate("vocabulary_progress_sort_newest")}
            </option>
            <option value="OLDEST_ADDED">
              {translate("vocabulary_progress_sort_oldest")}
            </option>
            <option value="MOST_PRACTICED">
              {translate("vocabulary_progress_sort_most_practiced")}
            </option>
          </select>
        </div>
      </section>

      <section>
        <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="text-xl font-semibold text-slate-900">
              {translate("vocabulary_progress_table_title")}
            </h2>
            <div className="grid grid-cols-1 gap-1 text-sm text-slate-500 sm:text-right">
              <p>
                {translate("vocabulary_progress_table_showing")}{" "}
                <span className="font-semibold text-slate-700">
                  {formatNumber(totalVocabularyCount)}
                </span>
              </p>
              <p>
                {translate("vocabulary_progress_workspace_selection_label")}:{" "}
                <span className="font-semibold text-slate-700">
                  {effectiveLanguageFilters.length === 0
                    ? `${translate("spaced_preview_language_all")} (${workspaceVisibleTotalCount})`
                    : `${effectiveLanguageFilters.length}`}
                </span>
              </p>
              <p>
                {translate("vocabulary_progress_workspace_total_hint", {
                  total: formatNumber(totalVocabularyCount),
                })}
              </p>
            </div>
          </div>
        </div>

        {visibleVocabularyCount === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-600">
            {translate("vocabulary_progress_empty_state")}
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <TableHeader>{translate("vocabulary_progress_table_word")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_translation")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_stage")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_streak")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_last_practiced")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_next_review")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_actions")}</TableHeader>
                  </tr>
                </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                  {sortedVocabulary.map((vocabulary) => (
                    <VocabularyTableRow
                      key={vocabulary.id}
                      vocabulary={vocabulary}
                      translate={translate}
                      showMoreGrammar={showMoreGrammar}
                      setShowMoreGrammar={setShowMoreGrammar}
                      onDeleteRequest={handleDeleteRequest}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {sortedVocabulary.map((vocabulary) => (
                <VocabularyCardRow
                  key={vocabulary.id}
                  vocabulary={vocabulary}
                  translate={translate}
                  showMoreGrammar={showMoreGrammar}
                  setShowMoreGrammar={setShowMoreGrammar}
                  onDeleteRequest={handleDeleteRequest}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {isFetchingNextPage && (
        <p className="text-center text-sm text-slate-500">
          {translate("vocabulary_progress_loading_more")}
        </p>
      )}
      {hasNextPage && !isFetchingNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => {
              void fetchNextPage();
            }}
            className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-500"
          >
            {translate("vocabulary_progress_load_more")}
          </button>
        </div>
      )}
      {vocabularyToDelete && (
        <DeleteVocabularyDialog
          vocabulary={vocabularyToDelete}
          translate={translate}
          isLoading={deleteVocabulary.isPending}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

function VocabularyTableRow({
  vocabulary,
  translate,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryTranslation = useMemo(
    () =>
      getVocabularyPrimaryTranslation({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryWord = useMemo(
    () =>
      getVocabularyPrimaryWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const surfaceWord = useMemo(
    () =>
      getVocabularySurfaceWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const baseWord = useMemo(
    () =>
      getVocabularyBaseWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");

  const lastPracticedCopy = vocabulary.last_tested
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(vocabulary.last_tested))
    : translate("progress_never");

  return (
    <tr className="hover:bg-slate-50">
      <TableCell>
        <div className="flex items-center gap-2">
          <p className="font-semibold">{primaryWord}</p>
          <VocabularyPronunciationButton vocabulary={vocabulary} />
        </div>
        {surfaceWord !== primaryWord && (
          <p className="text-xs text-slate-500">
            {translate("vocabulary_label_seen_form")}: {surfaceWord}
          </p>
        )}
        <p className="text-xs text-slate-500">
          {vocabulary.word_language.replaceAll("_", " ")}
        </p>
      </TableCell>
      <TableCell>
        <p>{primaryTranslation}</p>
        {baseWord && (
          <p className="mt-1 text-left text-xs text-slate-600">
            <span className="font-semibold">
              {translate("vocabulary_label_base_form")}:
            </span>{" "}
            {baseWord}
          </p>
        )}
        <p className="mt-1 text-left text-xs text-slate-700">
          {primarySentence
            ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
          {translationDetails.grammar.part_of_speech && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_pos")}:{" "}
              {translationDetails.grammar.part_of_speech}
            </span>
          )}
          {translationDetails.grammar.tense && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_tense")}:{" "}
              {translationDetails.grammar.tense}
            </span>
          )}
          {translationDetails.grammar.number && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_number")}:{" "}
              {translationDetails.grammar.number}
            </span>
          )}
          {translationDetails.grammar.gender && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_gender")}:{" "}
              {translationDetails.grammar.gender}
            </span>
          )}
          {translationDetails.source_plural_form && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_plural")}:{" "}
              {translationDetails.source_plural_form}
            </span>
          )}
        </div>
        {showMoreGrammar && (
          <div className="mt-2 space-y-1 text-xs text-slate-600">
            {translationDetails.grammar.mood && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_mood")}:
                </span>{" "}
                {translationDetails.grammar.mood}
              </p>
            )}
            {translationDetails.grammar.person && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_person")}:
                </span>{" "}
                {translationDetails.grammar.person}
              </p>
            )}
            {translationDetails.grammar.case && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_case")}:
                </span>{" "}
                {translationDetails.grammar.case}
              </p>
            )}
            {translationDetails.grammar.syntactic_function && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_syntactic_function")}:
                </span>{" "}
                {translationDetails.grammar.syntactic_function}
              </p>
            )}
            {translationDetails.alternative_meanings && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_alternatives")}:
                </span>{" "}
                {translationDetails.alternative_meanings}
              </p>
            )}
            {translationDetails.source_lemma &&
              translationDetails.source_lemma !== primaryWord &&
              translationDetails.source_lemma !== baseWord && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_lemma")}:
                  </span>{" "}
                  {translationDetails.source_lemma}
                </p>
              )}
            {translationDetails.source_context_hint && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_context_hint")}:
                </span>{" "}
                {translationDetails.source_context_hint}
              </p>
            )}
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => setShowMoreGrammar((prev) => !prev)}
            className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
          >
            {showMoreGrammar
              ? translate("vocabulary_less_grammar")
              : translate("vocabulary_more_grammar")}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <StageBadge stage={vocabulary.stage} translate={translate} />
          {vocabulary.stage === "LEARNING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_learning_interval")}
            </p>
          )}
          {vocabulary.stage === "REVIEWING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_reviewing_interval")}
            </p>
          )}
          {vocabulary.stage === "MASTERED" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_mastered_interval")}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span>{vocabulary.winning_streak}</span>
          <InformationCircleIcon className="h-4 w-4 text-slate-400" title={translate(streakStageKey(vocabulary.winning_streak))} />
        </div>
      </TableCell>
      <TableCell>{lastPracticedCopy}</TableCell>
      <TableCell>{nextReviewCopy}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              markVocabularyAsForgotten.mutateAsync({
                vocabularyId: vocabulary.id,
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
          >
            <HandThumbDownIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_forgotten")}
          </button>
          <button
            onClick={() =>
              markVocabularyAsLearned.mutateAsync({
                vocabularyId: vocabulary.id,
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
          >
            <HandThumbUpIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_learned")}
          </button>
          <button
            onClick={() => onDeleteRequest(vocabulary)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_delete")}
          </button>
        </div>
      </TableCell>
    </tr>
  );
}

function VocabularyCardRow({
  vocabulary,
  translate,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryTranslation = useMemo(
    () =>
      getVocabularyPrimaryTranslation({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const primaryWord = useMemo(
    () =>
      getVocabularyPrimaryWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const surfaceWord = useMemo(
    () =>
      getVocabularySurfaceWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const baseWord = useMemo(
    () =>
      getVocabularyBaseWord({
        word: vocabulary.word,
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.word, vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-slate-900">
              {primaryWord}
            </p>
            <VocabularyPronunciationButton vocabulary={vocabulary} />
          </div>
          <p className="text-sm text-slate-500">
            {primaryTranslation}
          </p>
          {baseWord && (
            <p className="mt-1 text-left text-xs text-slate-600">
              <span className="font-semibold">
                {translate("vocabulary_label_base_form")}:
              </span>{" "}
              {baseWord}
            </p>
          )}
          {surfaceWord !== primaryWord && (
            <p className="mt-1 text-left text-xs text-slate-600">
              <span className="font-semibold">
                {translate("vocabulary_label_seen_form")}:
              </span>{" "}
              {surfaceWord}
            </p>
          )}
          <p className="mt-1 text-left text-xs text-slate-700">
            {primarySentence
              ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
              : translate("vocabulary_source_sentence_unavailable")}
          </p>
          {secondarySentence && (
            <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            {translationDetails.grammar.part_of_speech && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_pos")}:{" "}
                {translationDetails.grammar.part_of_speech}
              </span>
            )}
            {translationDetails.grammar.tense && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_tense")}:{" "}
                {translationDetails.grammar.tense}
              </span>
            )}
            {translationDetails.grammar.number && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_number")}:{" "}
                {translationDetails.grammar.number}
              </span>
            )}
            {translationDetails.grammar.gender && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_gender")}:{" "}
                {translationDetails.grammar.gender}
              </span>
            )}
            {translationDetails.source_plural_form && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_plural")}:{" "}
                {translationDetails.source_plural_form}
              </span>
            )}
          </div>
          {showMoreGrammar && (
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              {translationDetails.grammar.mood && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_mood")}:
                  </span>{" "}
                  {translationDetails.grammar.mood}
                </p>
              )}
              {translationDetails.grammar.person && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_person")}:
                  </span>{" "}
                  {translationDetails.grammar.person}
                </p>
              )}
              {translationDetails.grammar.case && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_case")}:
                  </span>{" "}
                  {translationDetails.grammar.case}
                </p>
              )}
              {translationDetails.grammar.syntactic_function && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_syntactic_function")}:
                  </span>{" "}
                  {translationDetails.grammar.syntactic_function}
                </p>
              )}
              {translationDetails.alternative_meanings && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_alternatives")}:
                  </span>{" "}
                  {translationDetails.alternative_meanings}
                </p>
              )}
              {translationDetails.source_lemma &&
                translationDetails.source_lemma !== primaryWord &&
                translationDetails.source_lemma !== baseWord && (
                  <p>
                    <span className="font-semibold">
                      {translate("vocabulary_label_lemma")}:
                    </span>{" "}
                    {translationDetails.source_lemma}
                  </p>
                )}
              {translationDetails.source_context_hint && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_context_hint")}:
                  </span>{" "}
                  {translationDetails.source_context_hint}
                </p>
              )}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setShowMoreGrammar((prev) => !prev)}
              className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
            >
              {showMoreGrammar
                ? translate("vocabulary_less_grammar")
                : translate("vocabulary_more_grammar")}
            </button>
          </div>
        </div>
        <StageBadge stage={vocabulary.stage} translate={translate} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-600">
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_streak_label")}
          </p>
          <p className="font-semibold">{vocabulary.winning_streak}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_next_review")}
          </p>
          <p className="font-semibold">{nextReviewCopy}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_last_practiced")}
          </p>
          <p className="font-semibold">
            {vocabulary.last_tested
              ? new Intl.DateTimeFormat(undefined, {
                  month: "short",
                  day: "numeric",
                }).format(new Date(vocabulary.last_tested))
              : translate("progress_never")}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("vocabulary_progress_table_language")}
          </p>
          <p className="font-semibold">
            {vocabulary.word_language.replaceAll("_", " ")}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            markVocabularyAsForgotten.mutateAsync({
              vocabularyId: vocabulary.id,
            })
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
        >
          <HandThumbDownIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_forgotten")}
        </button>
        <button
          onClick={() =>
            markVocabularyAsLearned.mutateAsync({
              vocabularyId: vocabulary.id,
            })
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
        >
          <HandThumbUpIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_learned")}
        </button>
        <button
          onClick={() => onDeleteRequest(vocabulary)}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
        >
          <TrashIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_delete")}
        </button>
      </div>
    </div>
  );
}

function TableHeader({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
      {children}
    </th>
  );
}

function TableCell({ children }: { children: ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function StageBadge({
  stage,
  translate,
}: {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const colorMap: Record<Exclude<VocabularyStageWithAll, "ALL">, string> = {
    DUE: "bg-rose-100 text-rose-800",
    NEW: "bg-blue-100 text-blue-800",
    LEARNING: "bg-amber-100 text-amber-800",
    REVIEWING: "bg-indigo-100 text-indigo-800",
    MASTERED: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-center ${colorMap[stage]}`}
    >
      {translate(
        STAGE_META.find((meta) => meta.id === stage)?.labelKey ??
          "progress_stage_new",
      )}
    </span>
  );
}

function DeleteVocabularyDialog({
  vocabulary,
  translate,
  isLoading,
  onCancel,
  onConfirm,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-8">
      <div className="w-full max-w-md rounded-2xl bg-white p-6">
        <h3 className="text-lg font-semibold text-slate-900">
          {translate("vocabulary_progress_delete_title")}
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {translate("vocabulary_progress_delete_message", {
            word: vocabulary.word,
          })}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors disabled:opacity-50"
            onClick={onCancel}
            disabled={isLoading}
          >
            {translate("vocabulary_progress_delete_cancel")}
          </button>
          <button
            type="button"
            className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? translate("vocabulary_progress_delete_pending")
              : translate("vocabulary_progress_delete_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
      }`}
      type="button"
    >
      {label}
    </button>
  );
}

function SrsInfoPanel({
  translate,
}: {
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_srs_heading")}
          </p>
          <p className="text-base font-semibold text-slate-900">
            {translate("vocabulary_progress_srs_title")}
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600">
          <InformationCircleIcon className="h-4 w-4" />
          {translate(
            open
              ? "vocabulary_progress_srs_toggle_hide"
              : "vocabulary_progress_srs_toggle_show",
          )}
        </div>
      </button>
      {open && (
        <div className="mt-4 space-y-4 text-sm text-slate-600">
          <p>{translate("vocabulary_progress_srs_intro")}</p>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_actions_heading")}
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>{translate("vocabulary_progress_srs_action_known")}</li>
              <li>{translate("vocabulary_progress_srs_action_almost")}</li>
              <li>{translate("vocabulary_progress_srs_action_unknown")}</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_schedule_heading")}
            </p>
            <dl className="mt-2 grid gap-3 sm:grid-cols-2">
              {SRS_SCHEDULE.map((entry) => (
                <div
                  key={entry.streakLabel}
                  className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                >
                  <dt className="text-xs uppercase tracking-wide text-slate-500">
                    {translate("vocabulary_progress_srs_streak_label", {
                      streak: entry.streakLabel,
                    })}
                  </dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {translate(entry.translationKey)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="text-xs text-slate-500">
            {translate("vocabulary_progress_srs_due_hint")}
          </p>
        </div>
      )}
    </div>
  );
}
